/**
 * Black-Scholes Option Pricing Model
 * Calculates theoretical price for European Call options
 */

/**
 * Cumulative distribution function for standard normal distribution
 */
function normalCDF(x) {
  const t = 1 / (1 + 0.2316419 * Math.abs(x));
  const d = 0.3989423 * Math.exp((-x * x) / 2);
  const probability =
    d *
    t *
    (0.3193815 +
      t * (-0.3565638 + t * (1.781478 + t * (-1.821256 + t * 1.330274))));

  return x > 0 ? 1 - probability : probability;
}

/**
 * Calculate d1 parameter for Black-Scholes
 */
function calculateD1(S, K, T, r, sigma) {
  return (
    (Math.log(S / K) + (r + (sigma * sigma) / 2) * T) / (sigma * Math.sqrt(T))
  );
}

/**
 * Calculate d2 parameter for Black-Scholes
 */
function calculateD2(d1, sigma, T) {
  return d1 - sigma * Math.sqrt(T);
}

/**
 * Calculate Call Option Price using Black-Scholes
 *
 * @param {number} S - Current stock price
 * @param {number} K - Strike price
 * @param {number} T - Time to expiration in years
 * @param {number} r - Risk-free interest rate (as decimal, e.g., 0.045 for 4.5%)
 * @param {number} sigma - Volatility (IV as decimal, e.g., 0.50 for 50%)
 * @param {number} q - Dividend yield (as decimal, default 0)
 * @returns {number} - Call option theoretical price
 */
export function blackScholesCall(S, K, T, r, sigma, q = 0) {
  // Handle edge cases
  if (T <= 0) {
    // At expiration or past, intrinsic value only
    return Math.max(S - K, 0);
  }

  if (sigma <= 0) {
    // No volatility, return intrinsic value
    return Math.max(S - K, 0);
  }

  // Adjust stock price for dividends
  const adjustedS = S * Math.exp(-q * T);

  const d1 = calculateD1(adjustedS, K, T, r, sigma);
  const d2 = calculateD2(d1, sigma, T);

  const callPrice =
    adjustedS * normalCDF(d1) - K * Math.exp(-r * T) * normalCDF(d2);

  return Math.max(callPrice, 0); // Price cannot be negative
}

/**
 * Calculate option Greeks (optional, for future use)
 */
export function calculateGreeks(S, K, T, r, sigma, q = 0) {
  if (T <= 0) {
    return { delta: S > K ? 1 : 0, gamma: 0, theta: 0, vega: 0 };
  }

  const adjustedS = S * Math.exp(-q * T);
  const d1 = calculateD1(adjustedS, K, T, r, sigma);
  const d2 = calculateD2(d1, sigma, T);

  // Delta: Rate of change of option price with respect to stock price
  const delta = Math.exp(-q * T) * normalCDF(d1);

  // Gamma: Rate of change of delta with respect to stock price
  const gamma =
    (Math.exp(-q * T) * Math.exp((-d1 * d1) / 2)) /
    (adjustedS * sigma * Math.sqrt(2 * Math.PI * T));

  // Theta: Rate of change of option price with respect to time (per day)
  const theta =
    ((-adjustedS * Math.exp((-d1 * d1) / 2) * sigma) /
      (2 * Math.sqrt(2 * Math.PI * T)) -
      r * K * Math.exp(-r * T) * normalCDF(d2) +
      q * adjustedS * normalCDF(d1)) /
    365;

  // Vega: Rate of change of option price with respect to volatility
  const vega =
    (adjustedS * Math.exp((-d1 * d1) / 2) * Math.sqrt(T)) /
    Math.sqrt(2 * Math.PI);

  return { delta, gamma, theta, vega };
}
